"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { api, Case, freeBlockReason, localizedComplaint, localizedName, localizedReferralReason, parseApiError, SubscriptionStatus } from "@/lib/api";
import { ModalOverlay } from "@/components/ModalOverlay";
import { PageHelp } from "@/components/PageHelp";
import { useRequireAuth } from "@/lib/useRequireAuth";
import { getSubscriptionStatus } from "@/lib/subscriptionStatus";
import { announceStorage, useLocalStorageValue } from "@/lib/useClientValue";
import { intlLocale } from "@/i18n/config";
// Per-case practice state as the dashboard needs it: how many sessions the student
// has completed with this case (in the current cycle) and when the last one ended.
// `count` süpervizör raporunu BEKLEMEZ — seans biter bitmez danışan listeye girer.
type CaseMeta = { count: number; lastAt: string | null };
type PickerTab = "library" | "custom";
type View = "card" | "list";

// How many ongoing clients show before the list folds behind "All (N)".
const ONGOING_PREVIEW = 3;

// Gövde metni uygulamanın geri kalanında Inter; bu sayfa (ve "+"tan büyüyen vaka
// seçici) tasarım tuvaliyle birebir olsun diye başlık fontunu — `--font-lora`
// değişkenine yüklenen Plus Jakarta Sans — metnin tamamına verir.
const PAGE_FONT = "var(--font-lora), system-ui, sans-serif";

// Free = one-time trial: three sessions IN TOTAL with ONE client. The banner
// shows the lifetime counter, never a monthly one.
function FreeBanner({ status }: { status: SubscriptionStatus }) {
  const t = useTranslations("dashboard.free");
  const used = status.sessions_used_total ?? 0;
  const limit = status.sessions_limit ?? 3;
  return (
    <div
      className="flex items-center justify-between rounded-xl px-4 py-3 text-sm"
      style={{ background: 'var(--accent-light)', border: '1px solid rgba(15,76,76,0.12)', color: 'var(--accent)' }}
    >
      <span>
        <span className="font-semibold">{t("banner")}</span> — {t("used", { used, limit })}
      </span>
      <Link
        href="/pricing"
        className="ml-4 shrink-0 rounded-lg px-3 py-1 text-xs font-semibold text-white hover:opacity-90"
        style={{ background: 'var(--accent)' }}
      >
        {t("upgrade")}
      </Link>
    </div>
  );
}

// "3 days ago" / "yesterday" / "today" in the UI language. Coarsens to weeks and
// months so an old case never reads "48 days ago".
function relativeWhen(iso: string, locale: string): string {
  const days = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 86_400_000));
  const rtf = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: "auto" });
  if (days < 7) return rtf.format(-days, "day");
  if (days < 30) return rtf.format(-Math.round(days / 7), "week");
  return rtf.format(-Math.round(days / 30), "month");
}

const PersonGlyph = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round">
    <circle cx="12" cy="8.5" r="3.6" />
    <path d="M5 19.5c0-3.4 3.1-5.6 7-5.6s7 2.2 7 5.6" />
  </svg>
);

// "Yeni hasta" düğmesinin artısı. Metnin İÇİNDE değil ikon olarak duruyor:
// `customPatients.newButton` çevirileri eskiden "+ Yeni Hasta" diye başlıyordu ve
// ikon eklenince artı iki kez yazılıyordu. Artı artık on bir dilde de ikondan gelir.
const LockGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" aria-hidden="true">
    <rect x="5" y="10.5" width="14" height="9.5" rx="2" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
  </svg>
);

const PlusGlyph = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
    <path d="M12 6v12M6 12h12" />
  </svg>
);

const primaryBtn =
  "rounded-[11px] text-[13.5px] font-semibold text-white transition-colors";
const secondaryBtn =
  "rounded-[11px] border border-[var(--border)] text-[13.5px] font-medium text-[var(--text-secondary)] transition-colors hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]";

// Okunmamış süpervizör raporu rozeti. Rapor ürünün ürettiği en pahalı şey ve
// "hazır" bildirimi kaçması kolay; sayı, üç birikmiş raporun bir tanesiyle aynı
// şey olmadığını söylüyor.
function UnreadBadge({ unread }: { unread: number }) {
  if (unread <= 0) return null;
  return (
    <span
      aria-hidden
      className="absolute -top-1.5 -right-1.5 h-[18px] min-w-[18px] rounded-full px-1 text-center text-[10px] font-bold leading-[18px] text-white shadow-sm"
      style={{ background: "var(--accent-warm)" }}
    >
      {unread > 9 ? "9+" : unread}
    </span>
  );
}

// One "ongoing client" row: a case the student has already had at least one
// completed session with. Continue → new session; history → the session list.
function OngoingCard({ c, meta, unread, onContinue }: { c: Case; meta: CaseMeta; unread: number; onContinue: () => void }) {
  const t = useTranslations("dashboard.ongoing");
  const tCard = useTranslations("dashboard.caseCard");
  const locale = useLocale();
  const router = useRouter();
  const name = localizedName(c, locale);
  return (
    <div
      className="flex flex-col gap-3.5 rounded-2xl border p-4"
      style={{ background: "var(--cream)", borderColor: "var(--border-strong)" }}
    >
      <div className="flex items-center gap-3.5">
        <div
          className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px]"
          style={{ background: "var(--accent-light)", color: "var(--accent)" }}
        >
          <PersonGlyph />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-[15.5px] font-bold tracking-[-0.3px] text-[var(--text-primary)]">{name}</span>
            <span
              className="flex shrink-0 items-center gap-[5px] rounded-full px-[9px] py-[3px] text-[11px] font-semibold"
              style={{ background: "var(--accent-light)", color: "var(--accent)" }}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--accent)" }} />
              {t("badge")}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-[9px] text-[12.5px] text-[var(--text-muted)]">
            <span>{tCard("sessionsCompleted", { count: meta.count })}</span>
            {meta.lastAt && (
              <>
                <span className="h-1 w-1 rounded-full" style={{ background: "var(--border-strong)" }} />
                <span>{t("lastSession", { when: relativeWhen(meta.lastAt, locale) })}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={onContinue}
          className={`${primaryBtn} px-4 py-2.5`}
          style={{ background: "var(--accent)" }}
          onMouseOver={e => (e.currentTarget.style.background = "var(--accent-mid)")}
          onMouseOut={e => (e.currentTarget.style.background = "var(--accent)")}
        >
          {t("continue")}
        </button>
        <span className="relative inline-flex">
          <button
            onClick={() => router.push(`/feedback/${c.id}?patient=${encodeURIComponent(name)}`)}
            className={`${secondaryBtn} px-3.5 py-2.5`}
            style={{ background: "var(--surface)" }}
            title={tCard("viewHistory")}
          >
            {t("history")}
            {unread > 0 && <span className="sr-only"> ({tCard("unreadReports", { count: unread })})</span>}
          </button>
          <UnreadBadge unread={unread} />
        </span>
      </div>
    </div>
  );
}

// Vaka seçicinin içindeki kart. Kütüphane kartından farkı bilinçli: burada vaka
// SEÇİLİYOR, o yüzden şikâyet metni kısaltılmadan tamamı duruyor ve kartın
// kendisi tıklanabilir bir seçim yüzeyi.
// `locked` = a free account already bound to ANOTHER client (or out of free
// sessions): the start button turns into an "unlock with a plan" call that
// routes to pricing — the card stays readable so the student sees what a plan
// opens up.
function PickerCaseCard({
  c, count, unread, selected, wide, locked, onSelect, onStart,
}: {
  c: Case; count: number; unread: number; selected: boolean; wide: boolean; locked: boolean;
  onSelect: () => void; onStart: () => void;
}) {
  const t = useTranslations("dashboard.caseCard");
  const locale = useLocale();
  const router = useRouter();
  const name = localizedName(c, locale);
  const reason = localizedReferralReason(c, locale);
  const complaint = localizedComplaint(c, locale);
  const frame = selected
    ? "border-[var(--accent)] shadow-[0_18px_40px_-28px_rgba(15,42,34,.5)]"
    : "border-[var(--border)] shadow-[0_1px_2px_rgba(15,42,34,.04)] hover:border-[var(--border-strong)] hover:shadow-[0_12px_30px_-22px_rgba(15,42,34,.35)]";
  return (
    <article
      onClick={onSelect}
      className={`flex cursor-pointer flex-col gap-3.5 rounded-[18px] border p-[22px] transition-[border-color,box-shadow] duration-200 ${frame} ${wide ? "col-span-full" : ""}`}
      style={{ background: "var(--surface)" }}
    >
      <div className="flex items-center gap-3">
        <div
          className="grid h-[42px] w-[42px] shrink-0 place-items-center rounded-[13px]"
          style={{ background: "var(--accent-light)", color: "var(--accent)" }}
        >
          <PersonGlyph />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-[16.5px] font-bold tracking-[-0.4px] text-[var(--text-primary)]">{name}</span>
          <span className="truncate text-[12.5px] text-[var(--text-muted)]">{t("age", { age: c.age })}</span>
        </div>
      </div>

      {/* Karar 41: zorluk rozeti yok; kartın etiketi hastanın GELME SEBEBİ.
          Sebep yoksa (kod öncesi eski özel vaka) satır hiç çizilmez. */}
      {(reason || c.assignment) && (
        <div className="flex flex-wrap gap-1.5">
          {reason && (
            <span
              className="self-start rounded-full px-[11px] py-[5px] text-[11.5px] font-semibold leading-[1.4]"
              style={{ background: "var(--accent-light)", color: "var(--text-secondary)" }}
            >
              {reason}
            </span>
          )}
          {/* Faz 4: canlı kohort ödevi. Eğitmen sıfırlamayı kapattıysa öğrenci
              bunu kartta görür (sunucu 403 reset_locked döner). */}
          {c.assignment && (
            <span
              className="self-start rounded-full px-[11px] py-[5px] text-[11.5px] font-semibold leading-[1.4]"
              style={{ background: "var(--cream)", color: "var(--accent)", border: "1px solid var(--border-strong)" }}
              title={c.assignment.title ?? undefined}
            >
              {t("cohortAssignment")}
              {c.assignment.title ? ` · ${c.assignment.title}` : ""}
              {!c.assignment.reset_allowed ? ` · ${t("resetLocked")}` : ""}
            </span>
          )}
        </div>
      )}

      <p className="text-[14px] leading-[1.62] text-[var(--text-secondary)]" style={{ textWrap: "pretty" }}>
        {complaint}
      </p>

      <div className="mt-auto flex flex-col gap-3">
        <div className="flex items-center gap-2 text-[12px] text-[var(--text-ghost)]">
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: count > 0 ? "var(--accent)" : "var(--border-strong)" }}
          />
          <span>{t("sessionsCompleted", { count })}</span>
        </div>
        {/* Butonlar kartın seçim yüzeyinin üstünde: tıklama yukarı kabarmamalı. */}
        <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
          {locked ? (
            <button
              onClick={onStart}
              title={t("lockedHint")}
              className={`${secondaryBtn} flex min-w-[132px] flex-1 items-center justify-center gap-1.5 px-3.5 py-[11px]`}
              style={{ background: "var(--surface)" }}
            >
              <LockGlyph />
              {t("unlock")}
            </button>
          ) : (
            <button
              onClick={onStart}
              className={`${primaryBtn} min-w-[132px] flex-1 px-3.5 py-[11px]`}
              style={{ background: "var(--accent)" }}
              onMouseOver={e => (e.currentTarget.style.background = "var(--accent-mid)")}
              onMouseOut={e => (e.currentTarget.style.background = "var(--accent)")}
            >
              {count > 0 ? t("continueSession") : t("startSession")}
            </button>
          )}
          {/* Tek geçmiş kapısı: seans listesi ve süpervizör raporları aynı sayfada. */}
          <span className="relative inline-flex">
            <button
              onClick={() => router.push(`/feedback/${c.id}?patient=${encodeURIComponent(name)}`)}
              className={`${secondaryBtn} px-[13px] py-[11px]`}
              style={{ background: "var(--surface)" }}
              title={t("viewHistory")}
            >
              {t("sessions")}
              {unread > 0 && <span className="sr-only"> ({t("unreadReports", { count: unread })})</span>}
            </button>
            <UnreadBadge unread={unread} />
          </span>
        </div>
      </div>
    </article>
  );
}

export default function Dashboard() {
  const tHeader = useTranslations("dashboard.header");
  const tStats = useTranslations("dashboard.stats");
  const tOngoing = useTranslations("dashboard.ongoing");
  const tHero = useTranslations("dashboard.hero");
  const tPicker = useTranslations("dashboard.picker");
  const tCustom = useTranslations("dashboard.customPatients");
  const tFree = useTranslations("dashboard.free");
  const tView = useTranslations("dashboard.view");
  const tCommon = useTranslations("common");
  const tErrors = useTranslations("dashboard.errors");

  const [cases, setCases] = useState<Case[]>([]);
  const [caseMeta, setCaseMeta] = useState<Record<number, CaseMeta>>({});
  // patient_case_id -> number of supervisor reports the student has never opened.
  const [unreadReports, setUnreadReports] = useState<Record<number, number>>({});
  const [subStatus, setSubStatus] = useState<SubscriptionStatus | null>(null);
  // Görünüm tercihi localStorage'dan render sırasında okunur (F19); yazım aynı
  // sekmeye `announceStorage` ile duyurulur.
  const storedView = useLocalStorageValue("pp_cases_view");
  const view: View = storedView === "list" ? "list" : "card";
  const [loadError, setLoadError] = useState(false);
  // Seans başlatma hatası: sayfayı terk etmeden gösterilir (F4: beklenmeyen
  // hata da bu şeritte "tekrar dene" ile; ne `alert` ne fiyatlandırmaya atma).
  const [startError, setStartError] = useState<string | null>(null);
  const [retryCase, setRetryCase] = useState<Case | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [showAllOngoing, setShowAllOngoing] = useState(false);
  // The case picker modal grows out of the "+" tile; the origin is captured on open.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTab, setPickerTab] = useState<PickerTab>("library");
  const [pickerOrigin, setPickerOrigin] = useState("50% 50%");
  const [selectedCase, setSelectedCase] = useState<number | null>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const closingRef = useRef(false);
  const locale = useLocale();
  const router = useRouter();
  const ready = useRequireAuth();

  // Kept out of the main Promise.all on purpose: the unread badge is decorative, so
  // a failure here must never turn into the page-wide "couldn't load" error state.
  const loadUnreadReports = useCallback(() => {
    api.getUnreadReports()
      .then((data) => {
        const map: Record<number, number> = {};
        data.cases.forEach((entry) => { map[entry.patient_case_id] = entry.unread; });
        setUnreadReports(map);
      })
      .catch(() => { /* no badge is better than a broken dashboard */ });
  }, []);

  useEffect(() => {
    if (!ready) return;
    loadUnreadReports();

    // F3: sayfa dört istekle boyanır (vakalar, abonelik durumu, /me, okunmamış
    // rozeti). Vaka başına seans sayısı ve son seans tarihi `/cases` listesiyle
    // gelir; eskiden her vaka için ayrı `/cases/{id}/state` atılıyordu (~20 istek).
    Promise.all([
      api.getCases(),
      getSubscriptionStatus(),
      api.getMe(),
    ]).then(([loadedCases, status, meData]) => {
      // Hukuki onay yoksa (yarım kalmış kayıt) VEYA kabul edilen sürüm eskiyse
      // uygulamaya girilemez; metin güncellenince herkes onay ekranından geçer.
      if (!meData.consent_current) { router.replace("/consent"); return; }
      const map: Record<number, CaseMeta> = {};
      loadedCases.forEach((c) => {
        map[c.id] = { count: c.completed_sessions ?? 0, lastAt: c.last_session_at ?? null };
      });
      // Hastaları öğrencinin dilindeki görünen ada göre alfabetik sırala.
      setCases([...loadedCases].sort((a, b) => localizedName(a, locale).localeCompare(localizedName(b, locale), locale)));
      setCaseMeta(map);
      setSubStatus(status);
      setLoaded(true);
    }).catch(() => {
      // A genuine 401 already redirects to /login inside the request wrapper, so
      // reaching here means a non-auth failure (server/network). Don't bounce the
      // user to login (that reads as "you were logged out" and destroys trust) —
      // show a recoverable inline error with a retry instead.
      setLoadError(true);
    });
  }, [ready, router, reloadKey, loadUnreadReports, locale]);

  // Returning from a report normally remounts this page, which refetches on its own.
  // This covers the cases where it doesn't — a restored tab, or a report read in a
  // second tab — so the badge can never linger after the report has been read.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === "visible") loadUnreadReports();
    }
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [loadUnreadReports]);

  function changeView(v: View) {
    try { localStorage.setItem("pp_cases_view", v); } catch { /* özel pencere: tercih tutulmaz */ }
    announceStorage();
  }

  function openPicker() {
    const el = heroRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      setPickerOrigin(
        `${((r.left + r.width / 2) / window.innerWidth * 100).toFixed(1)}% ${((r.top + r.height / 2) / window.innerHeight * 100).toFixed(1)}%`,
      );
    }
    setPickerOpen(true);
  }

  // Kapanış, açılışın tersi: katman "+" kutusuna geri büzülür. Salt CSS ile
  // yapılamıyor çünkü öğe animasyon bitmeden DOM'dan kalkardı; bu yüzden WAAPI
  // ile oynatılıp `finished` beklendikten sonra state kapatılıyor.
  const closePicker = useCallback(() => {
    if (closingRef.current) return;
    const dialog = dialogRef.current;
    const done = () => { closingRef.current = false; setPickerOpen(false); };
    if (!dialog || typeof dialog.animate !== "function") { done(); return; }
    closingRef.current = true;
    overlayRef.current?.animate(
      [{ opacity: 1 }, { opacity: 0 }],
      { duration: 190, delay: 110, easing: "ease-in", fill: "forwards" },
    );
    dialog.animate(
      [
        { opacity: 1, transform: "scale(1)", offset: 0 },
        { opacity: 1, offset: 0.4 },
        { opacity: 0, transform: "scale(.18)", offset: 1 },
      ],
      { duration: 300, easing: "cubic-bezier(.75,0,.8,.25)", fill: "forwards" },
    ).finished.then(done, done);
  }, []);

  // Escape closes the picker, like every other modal in the app.
  useEffect(() => {
    if (!pickerOpen) return;
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") closePicker(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pickerOpen, closePicker]);

  async function proceedStart(c: Case) {
    const session = await api.startSession(c.id);
    router.push(`/session/${session.id}?patient=${encodeURIComponent(localizedName(c, locale))}&age=${c.age}`);
  }

  async function handleStart(c: Case) {
    // Free trial over (3 sessions used) or a second client on free: go straight
    // to pricing with the reason, no round-trip to the server needed.
    const known = freeBlockReason(subStatus, c.id);
    if (known) { router.push(`/pricing?reason=${known}`); return; }
    setStartError(null);
    setRetryCase(null);
    try {
      await proceedStart(c);
    } catch (e) {
      const { code } = parseApiError(e);
      // The server is the source of truth: a stale status still ends on pricing.
      if (code === "free_sessions_exhausted" || code === "free_case_locked") {
        router.push(`/pricing?reason=${code}`);
        return;
      }
      // Önceki seansın raporu hâlâ yazılıyor: hasta durumu o bitince güncellenir,
      // şimdi açılan seans bir önceki seansı hiç yaşanmamış gibi görürdü. Sayfada
      // kal, "tekrar dene" göster — fiyatlandırmaya atmak yanlış cevaptı.
      if (code === "evaluation_in_progress") {
        setStartError(tErrors("evaluationInProgress"));
        setRetryCase(c);
        return;
      }
      // Aylık hak bitti: sunucu artık kodlu cevap veriyor, sebebi taşıyarak
      // fiyatlandırmaya git (metni oradaki çevrili satır yazar).
      if (code === "monthly_sessions_exhausted") {
        router.push(`/pricing?reason=${code}`);
        return;
      }
      // Vaka artık görünmüyor (kohort ödevi bitti, vaka silindi): listeyi
      // tazelemek doğru cevap, fiyatlandırma değil.
      if (code === "case_not_found") {
        setStartError(tErrors("caseNotFound"));
        setReloadKey((k) => k + 1);
        return;
      }
      // Beklenmeyen hata (ağ, 5xx): yerinde, çevrili ve tekrar denenebilir.
      // Eskiden sunucunun ham metni `alert`le gösterilip fiyatlandırmaya
      // atılıyordu; zaman aşımına uğrayan öğrenciye "plan satın al" deniyordu.
      setStartError(tErrors("startFailed"));
      setRetryCase(c);
    }
  }

  function handleCreatePatient() {
    router.push("/create-patient");
  }

  const isFree = subStatus?.tier === "free";
  const isPro = subStatus?.tier === "pro";

  const libraryCases = cases.filter((c) => !c.is_custom);
  const customCases = cases.filter((c) => c.is_custom);
  // A case is "ongoing" once at least one session has been completed with it, most
  // recently practised first — that is the one the student most likely wants back.
  const ongoing = cases
    .filter((c) => (caseMeta[c.id]?.count ?? 0) > 0)
    .sort((a, b) => (caseMeta[b.id]?.lastAt ?? "").localeCompare(caseMeta[a.id]?.lastAt ?? ""));
  const ongoingShown = showAllOngoing ? ongoing : ongoing.slice(0, ONGOING_PREVIEW);
  const pickerCases = pickerTab === "library" ? libraryCases : customCases;

  function segClass(active: boolean) {
    return `grid h-[30px] w-8 place-items-center rounded-lg transition-colors ${
      active ? "text-[var(--accent)]" : "text-[var(--text-ghost)] hover:text-[var(--text-primary)]"
    }`;
  }

  const viewToggle = (
    <div className="flex items-center gap-0.5 rounded-[10px] border border-[var(--border)] p-[3px]" style={{ background: "var(--surface)" }}>
      <button
        onClick={() => changeView("list")}
        title={tView("list")}
        aria-label={tView("list")}
        className={segClass(view === "list")}
        style={view === "list" ? { background: "var(--accent-light)" } : undefined}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round"><path d="M4.5 7h15M4.5 12h15M4.5 17h15" /></svg>
      </button>
      <button
        onClick={() => changeView("card")}
        title={tView("card")}
        aria-label={tView("card")}
        className={segClass(view === "card")}
        style={view === "card" ? { background: "var(--accent-light)" } : undefined}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7}><path d="M4.5 4.5h6v6h-6zM13.5 4.5h6v6h-6zM4.5 13.5h6v6h-6zM13.5 13.5h6v6h-6z" /></svg>
      </button>
    </div>
  );

  function tabClass(active: boolean) {
    return `-mb-px whitespace-nowrap border-b-2 bg-transparent px-0.5 pb-3 text-[13.5px] font-semibold transition-colors ${
      active ? "border-[var(--accent)] text-[var(--text-primary)]" : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]"
    }`;
  }

  return (
    <>
      <main
        className="mx-auto flex w-full max-w-[1180px] flex-col gap-11 px-5 pt-14 pb-[72px] md:px-11"
        style={{ fontFamily: PAGE_FONT }}
      >
        <section className="flex flex-col gap-[22px]">
          {/* Header: eyebrow + title + hint on the left, live counts on the right. */}
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-[1.4px]" style={{ color: "var(--accent-warm)" }}>
                {tHeader("eyebrow")}
              </span>
              <h1 className="text-4xl font-bold leading-[1.05] tracking-[-1.3px] text-[var(--text-primary)]">
                {tHeader("title")}
              </h1>
              <p className="max-w-[44ch] text-[15px] text-[var(--text-secondary)]" style={{ textWrap: "pretty" }}>
                {tHeader("subtitle")}
              </p>
            </div>
            <div className="flex items-center gap-[22px]">
              {loaded && (
                <div className="flex gap-[22px]">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-2xl font-bold tracking-[-0.6px] text-[var(--text-primary)]">{ongoing.length}</span>
                    <span className="text-xs text-[var(--text-muted)]">{tStats("ongoing", { count: ongoing.length })}</span>
                  </div>
                  <div className="w-px" style={{ background: "var(--border)" }} />
                  <div className="flex flex-col gap-0.5">
                    <span className="text-2xl font-bold tracking-[-0.6px] text-[var(--text-primary)]">{libraryCases.length}</span>
                    <span className="text-xs text-[var(--text-muted)]">{tStats("ready", { count: libraryCases.length })}</span>
                  </div>
                </div>
              )}
              <PageHelp topic="dashboard" />
            </div>
          </div>

          {loadError && (
            <div
              className="flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm"
              style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}
            >
              <span>{tCommon("loadError")}</span>
              <button
                onClick={() => { setLoadError(false); setReloadKey((k) => k + 1); }}
                className="shrink-0 rounded-lg px-3 py-1 text-xs font-semibold text-white hover:opacity-90"
                style={{ background: '#b91c1c' }}
              >
                {tCommon("retry")}
              </button>
            </div>
          )}

          {startError && (
            <div
              className="flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm"
              style={{ background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }}
              role="status"
            >
              <span>{startError}</span>
              {retryCase && (
                <button
                  onClick={() => handleStart(retryCase)}
                  className="shrink-0 rounded-lg px-3 py-1 text-xs font-semibold text-white hover:opacity-90"
                  style={{ background: '#92400e' }}
                >
                  {tCommon("retry")}
                </button>
              )}
            </div>
          )}

          {isFree && subStatus && <FreeBanner status={subStatus} />}

          {/* Loading skeleton: holds the space of the clients area so the first paint
              is never an empty page that fills in a beat later. */}
          {!loaded && !loadError && (
            <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-[18px]" aria-busy="true" aria-live="polite">
              <div
                className="flex animate-pulse flex-col gap-3 rounded-[22px] border p-[22px]"
                style={{ background: "var(--surface)", borderColor: "var(--border)" }}
              >
                <div className="h-4 w-40 rounded-md" style={{ background: "var(--border)" }} />
                <div className="h-[72px] rounded-2xl" style={{ background: "var(--cream)" }} />
                <div className="h-[72px] rounded-2xl" style={{ background: "var(--cream)" }} />
              </div>
              <div className="h-[106px] animate-pulse rounded-3xl border-2 border-dashed" style={{ borderColor: "var(--border-strong)", background: "var(--cream)" }} />
            </div>
          )}

          {(loaded || loadError) && (
          <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-[18px]">
            {/* Ongoing clients — only once the student has actually practised with someone. */}
            {ongoing.length > 0 && (
              <section
                className="flex flex-col gap-3 rounded-[22px] border p-[22px] shadow-[0_1px_2px_rgba(15,42,34,.04)]"
                style={{ background: "var(--surface)", borderColor: "var(--border)" }}
              >
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-[15px] font-bold tracking-[-0.2px] text-[var(--text-primary)]">{tOngoing("title")}</h2>
                  {ongoing.length > ONGOING_PREVIEW && (
                    <button
                      onClick={() => setShowAllOngoing((v) => !v)}
                      className="text-[12.5px] font-semibold text-[var(--accent)] hover:text-[var(--text-primary)]"
                    >
                      {showAllOngoing ? tOngoing("less") : tOngoing("all", { count: ongoing.length })}
                    </button>
                  )}
                </div>
                {ongoingShown.map((c) => (
                  <OngoingCard
                    key={c.id}
                    c={c}
                    meta={caseMeta[c.id]}
                    unread={unreadReports[c.id] ?? 0}
                    onContinue={() => handleStart(c)}
                  />
                ))}
              </section>
            )}

            {/* The "+" tile: the single entry point to a new session. */}
            <div
              ref={heroRef}
              role="button"
              tabIndex={0}
              onClick={openPicker}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPicker(); } }}
              className="flex cursor-pointer items-center justify-center gap-5 rounded-3xl border-2 border-dashed px-6 py-5 text-left outline-none transition-colors hover:border-[var(--accent)] focus-visible:border-[var(--accent)]"
              style={{ borderColor: "var(--border-strong)", background: "var(--cream)" }}
              onMouseOver={e => (e.currentTarget.style.background = "var(--surface)")}
              onMouseOut={e => (e.currentTarget.style.background = "var(--cream)")}
            >
              <div
                className="grid h-16 w-16 shrink-0 place-items-center rounded-[26px] border-2 border-dashed"
                style={{ borderColor: "var(--border-strong)", background: "var(--bg)", color: "var(--accent)" }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"><path d="M12 5.5v13M5.5 12h13" /></svg>
              </div>
              <div className="flex flex-col gap-[3px]">
                <span className="text-base font-semibold tracking-[-0.3px] text-[var(--text-primary)]">{tHero("title")}</span>
                <span className="text-[13px] text-[var(--text-muted)]">{tHero("hint")}</span>
              </div>
            </div>
          </div>
          )}
        </section>
      </main>

      {/* Case picker. Grows out of the "+" tile (transform-origin captured on open). */}
      {pickerOpen && (
        <ModalOverlay
          ref={overlayRef}
          onClick={closePicker}
          className="fixed inset-0 z-40 flex items-center justify-center p-4 md:p-8"
          style={{ background: "rgba(15,42,34,.42)", backdropFilter: "blur(3px)", animation: "pp-fade .26s ease-out both", fontFamily: PAGE_FONT }}
        >
          <section
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={tPicker("title")}
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[88vh] w-full max-w-[1020px] flex-col gap-5 overflow-auto rounded-[22px] p-5 md:p-7"
            style={{
              background: "var(--bg)",
              boxShadow: "0 40px 90px -40px rgba(15,42,34,.6)",
              transformOrigin: pickerOrigin,
              animation: "pp-grow .42s cubic-bezier(.2,.75,.25,1) both",
            }}
          >
            <div className="flex flex-wrap items-center justify-between gap-[18px]">
              <div className="flex flex-col gap-1">
                <h2 className="text-[22px] font-bold tracking-[-0.6px] text-[var(--text-primary)]">
                  {tPicker("title")}
                </h2>
                <p className="text-[13.5px] text-[var(--text-muted)]">{tPicker("subtitle")}</p>
              </div>
              <div className="flex items-center gap-2">
                {viewToggle}
                <button
                  onClick={closePicker}
                  aria-label={tPicker("close")}
                  title={tPicker("close")}
                  className="grid h-[34px] w-[34px] place-items-center rounded-[10px] border border-[var(--border)] text-[var(--text-muted)] transition-colors hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]"
                  style={{ background: "var(--surface)" }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                </button>
              </div>
            </div>

            <div className="-mt-1.5 flex gap-[22px] border-b border-[var(--border)]">
              <button onClick={() => setPickerTab("library")} className={tabClass(pickerTab === "library")}>{tPicker("tabLibrary")}</button>
              <button onClick={() => setPickerTab("custom")} className={tabClass(pickerTab === "custom")}>{tPicker("tabCustom")}</button>
            </div>

            {pickerTab === "custom" && (
              <div className="flex items-end justify-between gap-4">
                <div className="flex flex-col gap-[3px]">
                  <h3 className="text-[17px] font-bold tracking-[-0.4px] text-[var(--text-primary)]">{tCustom("title")}</h3>
                  <p className="text-[13px] text-[var(--text-muted)]">{tCustom("description")}</p>
                </div>
                {isPro && customCases.length > 0 && (
                  <button
                    type="button"
                    onClick={handleCreatePatient}
                    className={`${primaryBtn} flex shrink-0 items-center gap-1.5 px-4 py-2`}
                    style={{ background: 'var(--accent)' }}
                    onMouseOver={e => (e.currentTarget.style.background = 'var(--accent-mid)')}
                    onMouseOut={e => (e.currentTarget.style.background = 'var(--accent)')}
                  >
                    <PlusGlyph />
                    {tCustom("newButton")}
                  </button>
                )}
              </div>
            )}

            {pickerCases.length > 0 ? (
              <div
                className="grid items-stretch gap-[18px]"
                style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}
              >
                {pickerCases.map((c) => (
                  <PickerCaseCard
                    key={c.id}
                    c={c}
                    count={caseMeta[c.id]?.count ?? 0}
                    unread={unreadReports[c.id] ?? 0}
                    selected={selectedCase === c.id}
                    wide={view === "list"}
                    locked={freeBlockReason(subStatus, c.id) !== null}
                    onSelect={() => setSelectedCase((prev) => (prev === c.id ? null : c.id))}
                    onStart={() => handleStart(c)}
                  />
                ))}
              </div>
            ) : pickerTab === "custom" ? (
              <div
                className="flex flex-col items-center gap-4 rounded-[14px] border-[1.5px] border-dashed px-[26px] py-11"
                style={{ borderColor: "var(--border-strong)", background: "var(--cream)" }}
              >
                <span className="text-center text-[13.5px] text-[var(--text-muted)]">
                  {isPro ? tCustom("empty") : tPicker("proOnly")}
                </span>
                {isPro ? (
                  <button
                    type="button"
                    onClick={handleCreatePatient}
                    className={`${primaryBtn} flex items-center gap-1.5 px-[18px] py-[11px]`}
                    style={{ background: 'var(--accent)' }}
                    onMouseOver={e => (e.currentTarget.style.background = 'var(--accent-mid)')}
                    onMouseOut={e => (e.currentTarget.style.background = 'var(--accent)')}
                  >
                    <PlusGlyph />
                    {tCustom("newButton")}
                  </button>
                ) : (
                  <Link
                    href="/pricing"
                    className={`${primaryBtn} px-[18px] py-[11px]`}
                    style={{ background: 'var(--accent)' }}
                  >
                    {tFree("upgrade")}
                  </Link>
                )}
              </div>
            ) : null}
          </section>
        </ModalOverlay>
      )}
    </>
  );
}
